import { useState, useEffect, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@hooks/useAuth';
import { adminCentersApi } from '@api/client';
import type { Center } from '@api/types';
import { PageHead } from '@components/admin/PageHead';
import { PasskeysCard } from './SettingsPage.PasskeysCard';
import { MyProfileCard } from './SettingsPage.MyProfileCard';
import { AccessControlCard } from './SettingsPage.AccessControlCard';
import { GeneralSection } from './SettingsPage.General';
import { ReservasSection } from './SettingsPage.Reservas';
import { NotificacionesSection, PagosSection, IntegracionesSection, MarcaPlataformaSection } from './SettingsPage.Placeholders';
import { ApiSection } from './SettingsPage.Api';
import { EmailsSection } from './SettingsPage.Emails';
import {
  DEFAULT_BH,
  bhFromApi,
  bhToApi,
  originsFromApi,
  originsToApi,
} from './SettingsPage.helpers';
import type { DayState } from './SettingsPage.helpers';

type SectionKey =
  | 'perfil'
  | 'general'
  | 'reservas'
  | 'notificaciones'
  | 'pagos'
  | 'integraciones'
  | 'marca'
  | 'emails'
  | 'roles'
  | 'api'
  | 'cuenta';

interface SectionDef {
  key: SectionKey;
  label: string;
  /** Si false, la sección queda separada del grupo previo por un divider. */
  contiguous?: boolean;
}

const ADMIN_SECTIONS: SectionDef[] = [
  { key: 'perfil',         label: 'Mi perfil' },
  { key: 'general',        label: 'General', contiguous: false },
  { key: 'reservas',       label: 'Reservas' },
  { key: 'notificaciones', label: 'Notificaciones' },
  { key: 'pagos',          label: 'Pagos' },
  { key: 'integraciones',  label: 'Integraciones' },
  { key: 'roles',          label: 'Roles y permisos' },
  { key: 'api',            label: 'API y webhooks' },
  { key: 'cuenta',         label: 'Mi cuenta', contiguous: false },
];

const SUPER_SECTIONS: SectionDef[] = [
  { key: 'perfil',        label: 'Mi perfil' },
  { key: 'marca',         label: 'Marca', contiguous: false },
  { key: 'emails',        label: 'Emails' },
  { key: 'integraciones', label: 'Integraciones' },
  { key: 'roles',         label: 'Roles y permisos' },
  { key: 'api',           label: 'API y webhooks' },
  { key: 'cuenta',        label: 'Mi cuenta', contiguous: false },
];

/**
 * Staff users solo ven Mi perfil + Mi cuenta. Las secciones del centro
 * (general, reservas, etc.) son de center_admin: aunque staff pudiera
 * llegar a /admin/settings, no debería ver controles que no le aplican.
 */
const STAFF_SECTIONS: SectionDef[] = [
  { key: 'perfil', label: 'Mi perfil' },
  { key: 'cuenta', label: 'Mi cuenta', contiguous: false },
];

export function SettingsPage() {
  const { user, hasRole } = useAuth();
  const qc = useQueryClient();
  const isSuper = hasRole('superadmin');
  const isMgr = hasRole('center_admin');

  const sections = isSuper ? SUPER_SECTIONS : isMgr ? ADMIN_SECTIONS : STAFF_SECTIONS;
  const [section, setSection] = useState<SectionKey>(sections[0].key);

  // Solo cargamos el centro para center_admin (el único rol que edita ajustes
  // del centro). Superadmin no tiene centro propio; staff no edita centros.
  const { data: center, isLoading } = useQuery<Center>({
    queryKey: ['center', 'me'],
    queryFn: () => adminCentersApi.getMe(),
    enabled: !!user && isMgr,
  });

  // State per-centre — solo se usa cuando el centro está cargado (center_admin).
  const [allowedOrigins, setAllowedOrigins] = useState('');
  const [confirmationMode, setConfirmationMode] = useState<'auto' | 'manual'>('auto');
  const [checkInEnabled, setCheckInEnabled] = useState(false);
  const [bhEnabled, setBhEnabled] = useState(false);
  const [bhDays, setBhDays] = useState<DayState[]>(() => DEFAULT_BH.map((d) => ({ ...d })));
  const [roomsAffectAvailability, setRoomsAffectAvailability] = useState(false);
  const [showRoomToCustomer, setShowRoomToCustomer] = useState(false);
  const [useSchedulesForAvailability, setUseSchedulesForAvailability] = useState(false);
  const [slotStepMinutes, setSlotStepMinutes] = useState<number>(15);
  const [pendingTtlHours, setPendingTtlHours] = useState<number>(24);
  const [approvalTtlHours, setApprovalTtlHours] = useState<number>(72);
  const [checkinCompletionBufferMinutes, setCheckinCompletionBufferMinutes] = useState<number>(30);

  useEffect(() => {
    if (!center) return;
    setAllowedOrigins(originsFromApi(center.allowedOrigins));
    setConfirmationMode(center.confirmationMode ?? 'auto');
    setCheckInEnabled(center.checkInEnabled ?? false);
    setBhEnabled(Array.isArray(center.businessHours));
    setBhDays(bhFromApi(center.businessHours));
    setRoomsAffectAvailability(center.roomsAffectAvailability ?? false);
    setShowRoomToCustomer(center.showRoomToCustomer ?? false);
    setUseSchedulesForAvailability(center.useSchedulesForAvailability ?? false);
    setSlotStepMinutes(center.slotStepMinutes ?? 15);
    setPendingTtlHours(center.pendingTtlHours ?? 24);
    setApprovalTtlHours(center.approvalTtlHours ?? 72);
    setCheckinCompletionBufferMinutes(center.checkinCompletionBufferMinutes ?? 30);
  }, [center]);

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => adminCentersApi.updateMe(data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['center', 'me'] }); },
  });

  function handleSave() {
    mutation.mutate({
      allowedOrigins: originsToApi(allowedOrigins),
      confirmationMode,
      checkInEnabled,
      businessHours: bhEnabled ? bhToApi(bhDays) : null,
      roomsAffectAvailability,
      showRoomToCustomer,
      useSchedulesForAvailability,
      slotStepMinutes,
      pendingTtlHours,
      approvalTtlHours,
      checkinCompletionBufferMinutes,
    });
  }

  // El botón "Guardar" se muestra solo cuando estamos en una sección con campos editables.
  const editableSections: SectionKey[] = ['general', 'reservas', 'api'];
  const showSaveBar = isMgr && editableSections.includes(section);

  if (isMgr && isLoading) {
    return (
      <div>
        <PageHead title="Configuración" sub="Ajustes del centro." />
        <div style={{ padding: 32, color: 'var(--fg-muted)' }}><span className="spinner" /></div>
      </div>
    );
  }

  let content: ReactNode = null;
  switch (section) {
    case 'perfil':
      content = <MyProfileCard />;
      break;
    case 'general':
      content = (
        <GeneralSection
          center={center}
          bhEnabled={bhEnabled}
          setBhEnabled={setBhEnabled}
          bhDays={bhDays}
          setBhDays={setBhDays}
        />
      );
      break;
    case 'reservas':
      content = (
        <ReservasSection
          confirmationMode={confirmationMode}
          setConfirmationMode={setConfirmationMode}
          checkInEnabled={checkInEnabled}
          setCheckInEnabled={setCheckInEnabled}
          slotStepMinutes={slotStepMinutes}
          setSlotStepMinutes={setSlotStepMinutes}
          pendingTtlHours={pendingTtlHours}
          setPendingTtlHours={setPendingTtlHours}
          approvalTtlHours={approvalTtlHours}
          setApprovalTtlHours={setApprovalTtlHours}
          checkinCompletionBufferMinutes={checkinCompletionBufferMinutes}
          setCheckinCompletionBufferMinutes={setCheckinCompletionBufferMinutes}
          roomsAffectAvailability={roomsAffectAvailability}
          setRoomsAffectAvailability={setRoomsAffectAvailability}
          showRoomToCustomer={showRoomToCustomer}
          setShowRoomToCustomer={setShowRoomToCustomer}
          useSchedulesForAvailability={useSchedulesForAvailability}
          setUseSchedulesForAvailability={setUseSchedulesForAvailability}
        />
      );
      break;
    case 'notificaciones':
      content = <NotificacionesSection />;
      break;
    case 'pagos':
      content = <PagosSection />;
      break;
    case 'integraciones':
      content = <IntegracionesSection />;
      break;
    case 'marca':
      content = <MarcaPlataformaSection />;
      break;
    case 'emails':
      content = <EmailsSection />;
      break;
    case 'roles':
      content = <AccessControlCard />;
      break;
    case 'api':
      content = (
        <ApiSection
          allowedOrigins={allowedOrigins}
          setAllowedOrigins={setAllowedOrigins}
          hidePerCentre={isSuper}
        />
      );
      break;
    case 'cuenta':
      content = <PasskeysCard />;
      break;
  }

  return (
    <div>
      <PageHead
        title="Configuración"
        sub={
          isSuper
            ? 'Ajustes de plataforma y control de acceso.'
            : isMgr
              ? `Ajustes del centro ${center?.name ?? ''}.`
              : 'Tu perfil y tu cuenta.'
        }
        actions={
          isMgr && center?.slug ? (
            <a
              href={`/widget/${center.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn"
              data-variant="secondary"
            >
              Ver widget de reservas
            </a>
          ) : undefined
        }
      />

      <div className="settings-layout">
        <aside className="settings-sidenav">
          {sections.map((s, i) => (
            <span key={s.key}>
              {i > 0 && s.contiguous === false && <div className="settings-sidenav-divider" />}
              <button
                type="button"
                className="settings-sidenav-item"
                data-active={section === s.key}
                onClick={() => { setSection(s.key); }}
              >
                {s.label}
              </button>
            </span>
          ))}
        </aside>

        <div className="settings-content">
          {content}

          {showSaveBar && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 32 }}>
              <button
                className="btn"
                data-variant="primary"
                onClick={handleSave}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
              </button>
              {mutation.isSuccess && (
                <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Cambios guardados ✓</span>
              )}
              {mutation.isError && (
                <span style={{ fontSize: 12, color: 'var(--danger)' }}>Error al guardar. Inténtalo de nuevo.</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
